/* Drawer upsell sizing and option sheet. Delegated events survive cart section refreshes. */
(() => {
  'use strict';

  const sizeKey = 'mw-cart-upsell-expanded';
  let expanded = false;
  let activeTrigger = null;
  let busy = false;
  try { expanded = localStorage.getItem(sizeKey) === 'true'; } catch (_) { /* Storage is optional. */ }

  const drawer = () => document.querySelector('#CartDrawer');
  const sheet = () => drawer()?.querySelector('[data-mw-upsell-sheet]');
  const activeContent = () => sheet()?.querySelector('[data-mw-upsell-option-content]');

  function applySize(root = document) {
    root.querySelectorAll?.('[data-mw-upsell-size]').forEach(section => {
      section.classList.toggle('mw-cart__upsell--compact', !expanded);
      section.classList.toggle('mw-cart__upsell--expanded', expanded);
      const toggle = section.querySelector('[data-mw-upsell-size-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(expanded));
        toggle.setAttribute('aria-label', expanded ? 'Make upsell products compact' : 'Expand upsell products');
      }
    });
  }

  function closeSheet(restoreFocus = true) {
    if (busy) return;
    const panel = sheet();
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    drawer()?.querySelector('[data-mw-upsell-scrim]')?.setAttribute('hidden', '');
    panel.querySelector('[data-mw-upsell-sheet-content]')?.replaceChildren();
    if (restoreFocus) activeTrigger?.focus?.();
    activeTrigger = null;
  }

  function currentVariant(content) {
    let variants;
    try { variants = JSON.parse(content.querySelector('[data-mw-upsell-variants]')?.textContent || '[]'); }
    catch (_) { return null; }
    const chosen = [...content.querySelectorAll('[data-mw-upsell-option]')].map(select => select.value);
    if (!chosen.length) return variants[0] || null;
    return variants.find(variant => variant.options.length === chosen.length && variant.options.every((value, index) => value === chosen[index])) || null;
  }

  function syncOptions() {
    const content = activeContent();
    if (!content) return;
    const variant = currentVariant(content);
    const form = content.querySelector('[data-mw-upsell-form]');
    const plan = form.querySelector('[data-mw-upsell-plan]');
    const planInput = form.querySelector('[data-mw-upsell-plan-input]');
    const submit = form.querySelector('[data-mw-upsell-submit]');
    const error = form.querySelector('[data-mw-upsell-error]');
    const validPlans = new Set((variant?.plans || []).map(allocation => String(allocation.id)));
    if (plan) {
      for (const option of plan.querySelectorAll('option')) {
        option.disabled = option.value ? !validPlans.has(option.value) : !!variant?.requires_selling_plan;
      }
      if (plan.selectedOptions[0]?.disabled) {
        plan.value = [...plan.querySelectorAll('option')].find(option => !option.disabled)?.value || '';
      }
    }
    const selectedPlan = plan?.value || '';
    const canAdd = !!variant?.available && (!variant.requires_selling_plan || !!selectedPlan && validPlans.has(selectedPlan));
    form.querySelector('[name="id"]').value = canAdd ? variant.id : '';
    planInput.disabled = !selectedPlan;
    planInput.value = selectedPlan;
    submit.disabled = !canAdd;
    submit.textContent = canAdd ? submit.dataset.addLabel : content.dataset.soldOut;
    if (error && !error.hidden) { error.hidden = true; error.textContent = ''; }
    if (variant) {
      const allocation = variant.plans.find(item => String(item.id) === selectedPlan);
      const amount = allocation?.price ?? variant.price;
      const formatter = window.Alpine?.store?.('xHelper')?.formatMoney;
      const money = content.dataset.moneyFormat;
      if (formatter && money) content.querySelector('[data-mw-upsell-price]').innerHTML = formatter.call(window.Alpine.store('xHelper'), amount, money);
    }
  }

  function openSheet(button) {
    const card = button.closest('[data-mw-upsell-product]');
    const template = card?.querySelector('template[data-mw-upsell-options]');
    const panel = sheet();
    const destination = panel?.querySelector('[data-mw-upsell-sheet-content]');
    if (!template || !destination || busy) return;
    activeTrigger = button;
    destination.replaceChildren(template.content.cloneNode(true));
    const submit = destination.querySelector('[data-mw-upsell-submit]');
    if (submit) submit.dataset.addLabel = submit.textContent.trim();
    panel.hidden = false;
    drawer()?.querySelector('[data-mw-upsell-scrim]')?.removeAttribute('hidden');
    syncOptions();
    panel.querySelector('[data-mw-upsell-close]')?.focus();
  }

  function showError(message) {
    const error = sheet()?.querySelector('[data-mw-upsell-error]');
    if (!error) return;
    error.textContent = message || 'Could not add this product. Please try again.';
    error.hidden = false;
    error.scrollIntoView?.({ block: 'nearest' });
  }

  function cartError(result) {
    if (typeof result?.description === 'string' && result.description.trim()) return result.description;
    if (typeof result?.message === 'string' && result.message.trim()) return result.message;
    const flatten = value => {
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.map(flatten).filter(Boolean).join(' ');
      if (value && typeof value === 'object') return Object.values(value).map(flatten).filter(Boolean).join(' ');
      return '';
    };
    return flatten(result?.errors) || 'Could not add this product. Please try again.';
  }

  async function submitUpsell(form) {
    if (busy) return;
    const content = activeContent();
    const variant = content && currentVariant(content);
    if (!variant?.available || String(form.querySelector('[name="id"]')?.value) !== String(variant.id)) return;
    const submit = form.querySelector('[data-mw-upsell-submit]');
    busy = true;
    submit.disabled = true;
    let added = false;
    let ownsCartLock = false;
    let releaseCart;
    let insuranceAdded = false;
    try {
      const helper = window.Alpine?.store?.('xCartHelper');
      if (!helper) throw new Error('Cart is not ready. Please try again.');
      releaseCart = await window.mwCart.acquire();
      ownsCartLock = true;
      let data = new FormData(form);
      const insuranceId = content.dataset.insuranceVariant;
      let insuranceRemoved = false;
      try { insuranceRemoved = !!localStorage.getItem('insuranceRemoved'); } catch (_) { /* Storage is optional. */ }
      const insuranceCookie = /(?:^|;)\s*eurus_insurance\s*=\s*([^;]+)/.exec(document.cookie || '');
      if (insuranceId && !insuranceRemoved && !insuranceCookie?.[1]?.trim()) {
        insuranceAdded = true;
        const purchase = new FormData();
        purchase.append('items[0][id]', data.get('id'));
        purchase.append('items[0][quantity]', '1');
        if (data.get('selling_plan')) purchase.append('items[0][selling_plan]', data.get('selling_plan'));
        purchase.append('items[1][id]', insuranceId);
        purchase.append('items[1][quantity]', '1');
        data = purchase;
      }
      data.append('sections', helper.getSectionsToRender().slice(0, 5).map(section => section.id));
      data.append('sections_url', window.location.pathname);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(window.Eurus?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      });
      added = response.ok;
      const result = await response.json();
      if (!response.ok || Number(result.status) >= 400) {
        added = false;
        throw new Error(cartError(result));
      }
      if (insuranceAdded) document.cookie = `eurus_insurance=${insuranceId}; path=/; SameSite=Lax`;
      document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: result } }));
      await helper.reRenderSections(result.sections);
      busy = false;
      closeSheet(false);
      window.Alpine?.store?.('xMiniCart')?.openCart?.();
      const replacement = drawer()?.querySelector(`[data-mw-upsell-product="${activeTrigger?.closest('[data-mw-upsell-product]')?.dataset.mwUpsellProduct}"] [data-mw-upsell-open]`);
      (replacement || drawer()?.querySelector('.mw-cart__checkout, #CloseCart'))?.focus?.();
      activeTrigger = null;
    } catch (error) {
      if (added) {
        window.location.assign(window.Eurus?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      } else {
        showError(error.message);
        submit.disabled = false;
      }
    } finally {
      busy = false;
      if (ownsCartLock) {
        releaseCart();
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      }
    }
  }

  document.addEventListener('click', event => {
    const target = event.target;
    const sizeToggle = target.closest('[data-mw-upsell-size-toggle]');
    if (sizeToggle?.closest('#CartDrawer')) {
      event.preventDefault();
      expanded = !expanded;
      try { localStorage.setItem(sizeKey, String(expanded)); } catch (_) { /* Storage is optional. */ }
      applySize(drawer());
      if (expanded) sizeToggle.closest('[data-mw-upsell-size]')?.querySelector('.splide')?.splide?.refresh?.();
      return;
    }
    const open = target.closest('[data-mw-upsell-open]');
    if (open?.closest('#CartDrawer')) { event.preventDefault(); openSheet(open); return; }
    if (target.closest('[data-mw-upsell-close], [data-mw-upsell-scrim]')) closeSheet();
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-mw-upsell-option], [data-mw-upsell-plan]') && event.target.closest('[data-mw-upsell-sheet]')) syncOptions();
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest?.('[data-mw-upsell-form]');
    if (!form || !form.closest('[data-mw-upsell-sheet]')) return;
    event.preventDefault();
    submitUpsell(form);
  });

  document.addEventListener('keydown', event => {
    const panel = sheet();
    if (!panel || panel.hidden) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeSheet(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]')].filter(element => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }, true);

  function init() {
    applySize();
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-mw-upsell-size], #CartDrawer') || node.querySelector?.('[data-mw-upsell-size]'))))) applySize();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('mw:cart:reset', () => { busy = false; closeSheet(false); activeTrigger = null; });
  document.addEventListener('mw:cart:upsells-updated', event => {
    const id = activeContent()?.dataset.productId || activeTrigger?.closest('[data-mw-upsell-product]')?.dataset.mwUpsellProduct;
    if (id && !event.detail.products.includes(id) && !busy) closeSheet();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
